import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { GitarreInteractivePathSection } from "@/components/GitarreInteractivePathSection";
import { GitarreTermineSection } from "@/components/GitarreTermineSection";
import { Video, Laptop, Users, CheckCircle, ArrowRight, Star, Award, Clock, Sparkles, BookOpen, PlayCircle, Music } from "lucide-react";
import heroImage from "@/assets/gitarre-hero-portrait.jpg";
import zertifikatImage from "@/assets/gitarre-zertifikat.jpg.asset.json";
import notesImg from "@/assets/notes-watercolor.png";
import drumImg from "@/assets/drum-watercolor.png";

// Module images
import gitarreKickstart from "@/assets/modules/gitarre-kickstart.jpg";
import gitarreGrundkursAkkorde from "@/assets/modules/gitarre-grundkurs-akkorde.jpg";
import gitarreAufbaukursBarregriffe from "@/assets/modules/gitarre-aufbaukurs-barregriffe.jpg";
import gitarreRhythmuskurs from "@/assets/modules/gitarre-rhythmuskurs.jpg";
import gitarreAufbaukursAkkorde from "@/assets/modules/gitarre-aufbaukurs-akkorde.jpg";
import gitarreLiederwerkstatt from "@/assets/modules/gitarre-liederwerkstatt.jpg";
import gitarreKreisspiele from "@/assets/modules/gitarre-kreisspiele.jpg";
import gitarreKinderlieder from "@/assets/modules/gitarre-kinderlieder.jpg";
import gitarreZupfmuster from "@/assets/modules/gitarre-zupfmuster.jpg";
import gitarreSchlagmuster from "@/assets/modules/gitarre-schlagmuster.jpg";
// Live-Webinar images
import gitarreEigenesLied from "@/assets/modules/gitarre-eigenes-lied.jpg";
import gitarreRhythmusHarmonie from "@/assets/modules/gitarre-rhythmus-harmonie.jpg";
import gitarrePopFolk from "@/assets/modules/gitarre-pop-folk.jpg";
import gitarreKreativArrangieren from "@/assets/modules/gitarre-kreativ-arrangieren.jpg";
import gitarreSpielenZahlen from "@/assets/modules/gitarre-spielen-zahlen.jpg";
import gitarreNotenMehr from "@/assets/modules/gitarre-noten-mehr.jpg";
import gitarreHarmoniewelten from "@/assets/modules/gitarre-harmoniewelten.jpg";
import teamPhoto from "@/assets/anne-daniel-team.jpg";
import teamSignatures from "@/assets/team-signatures-gitarre.png";

// Testimonial images
import danielaHartmann from "@/assets/testimonials/daniela-hartmann.jpg";
import fatmaUenal from "@/assets/testimonials/fatma-uenal.jpg";
import sandyTrinkies from "@/assets/testimonials/sandy-trinkies.jpg";
import SEO from "@/components/SEO";
import { formatEuro, getBundlePricingBySlug } from "@/config/trainingBundlePricing";

/** Preise aus der Anzeige-Config (dieselben Beträge wie die Kasse, Fragen 351 und 354). */
const GITARRE_PREISE = getBundlePricingBySlug("fachkraft-gitarre")?.tarife;
const GITARRE_PREIS = GITARRE_PREISE?.standard ? formatEuro(GITARRE_PREISE.standard.rechnung.amountCents) : "";
const GITARRE_MITGLIED = GITARRE_PREISE?.mitglied ? formatEuro(GITARRE_PREISE.mitglied.rechnung.amountCents) : "";

// Lernmodule
const modules = [
  {
    id: 1,
    title: "Dein eigenes Kinderlied gestalten",
    subtitle: "Kreativität trifft Musik",
    category: "Live-Webinare",
    image: gitarreEigenesLied,
    description: "In diesem Webinar entdeckst du, wie ein Kinderlied von der ersten Idee bis zur fertigen Melodie entsteht. Gemeinsam entwickeln wir Schritt für Schritt ein Lied, das nicht nur pädagogisch wertvoll ist, sondern auch die Herzen der Kinder erobert. Ob komplett neu oder auf Basis einer bekannten Melodie – wir tauchen in den kreativen Prozess ein und schaffen etwas Einzigartiges!",
    content: "- Wie entsteht ein Lied?\n- Wie ist ein Lied aufgebaut?\n- Alles selber machen oder eine bekannte Melodie oder einen bekannten Text verwenden?\n- Viel Praxis",
    instructor: "Anne Lüken",
    duration: "90 min",
  },
  {
    id: 2,
    title: "Rhythmus & Harmonie",
    subtitle: "Liedbegleitung für Kinderlieder meistern",
    category: "Live-Webinare",
    image: gitarreRhythmusHarmonie,
    description: "Tauche ein in die Welt des Rhythmus und der Harmonie! Lerne, wie du Kinderlieder mit lebendigen Akkorden und packenden Rhythmen begleiten kannst, um jedes Lied zu einem musikalischen Erlebnis zu machen.",
    content: "- Taktarten eines Liedes erkennen\n- Welche Schlag- und Zupfmuster können passen?\n- Ein Lied im 5er\n- Hilfe, es stehen keine Akkorde über den Noten!",
    instructor: "Anne Lüken",
    duration: "90 min",
  },
  {
    id: 3,
    title: "Von Pop bis Folk",
    subtitle: "Vielfalt für jedes Kinderlied",
    category: "Live-Webinare",
    image: gitarrePopFolk,
    description: "Entdecke die Vielfalt der Gitarrenmusik. Lerne verschiedene Spieltechniken und Stilrichtungen kennen! Von Pop bis Folk - und bereichere deine musikalische Praxis mit neuen, inspirierenden Ideen für das Spielen von Kinderliedern.",
    content: "- Verschiedene spezielle Anschlagsarten (Reggae, Powerchords, Folkpicking)\n- Ein Lied - viele Möglichkeiten\n- Inspiration bei YouTube finden",
    instructor: "Anne Lüken",
    duration: "90 min",
  },
  {
    id: 4,
    title: "Kreativ arrangieren",
    subtitle: "Kinderlieder neu entdecken",
    category: "Live-Webinare",
    image: gitarreKreativArrangieren,
    description: "Gestalte Kinderlieder kreativ neu! Dieses Webinar zeigt dir, wie du bestehende Lieder für verschiedene Altersgruppen und Situationen anpassen und so jedes Lied zu einem einzigartigen musikalischen Erlebnis machen kannst.",
    content: "- Wie führe ich ein neues Lied ein?\n- Lied mit Gesten begleiten\n- Lieder mit elementaren Instrumenten begleiten\n- Bewegungen finden",
    instructor: "Anne Lüken",
    duration: "90 min",
  },
  {
    id: 5,
    title: "Spielen nach Zahlen",
    subtitle: "Einfache Melodien mit der Tabulatur",
    category: "Live-Webinare",
    image: gitarreSpielenZahlen,
    description: "Entdecke den Zauber des Melodiespiels auf der Gitarre. Dieses Webinar führt dich Schritt für Schritt durch einfache Melodien und zeigt dir, wie du diese in deine musikalische Arbeit mit Kindern integrieren kannst.",
    content: "- Wechselschlag\n- Notenwerte, Rhythmus\n- Tabulatur\n- Skala über 2 Oktaven - Instrument entdecken",
    instructor: "Anne Lüken",
    duration: "90 min",
  },
  {
    id: 6,
    title: "Noten & mehr",
    subtitle: "Der Wegweiser durch die Welt der Gitarrennoten",
    category: "Live-Webinare",
    image: gitarreNotenMehr,
    description: "Erlerne die Kunst des Notenlesens speziell für Gitarre. Dieses Webinar bietet dir einen einfachen Einstieg in das Lesen von Noten, um deine musikalischen Fähigkeiten zu erweitern.",
    instructor: "Anne Lüken",
    duration: "90 min",
  },
  {
    id: 7,
    title: "Harmoniewelten erkunden",
    subtitle: "Quintenzirkel leicht gemacht",
    category: "Live-Webinare",
    image: gitarreHarmoniewelten,
    description: "Erkunde die Grundlagen der Harmonielehre und des Quintenzirkels. In diesem Webinar lernst du, wie du Akkordprogressionen verstehen und anwenden kannst, um deine Liedbegleitung zu bereichern.",
    content: "- Was ist der Quintenzirkel\n- Dur / Moll\n- Transponieren\n- Akkorde für ein Lied finden\n- Kadenzen spielen und finden\n- Ein Lied mit verschiedenen Akkorden begleiten",
    instructor: "Anne Lüken",
    duration: "90 min",
  },
  {
    id: 8,
    title: "Gitarren-Kickstart für pädagogische Fachkräfte",
    subtitle: "Der Einsteigerkurs",
    category: "Selbstlernmodule",
    image: gitarreKickstart,
    videoUrl: "https://www.loom.com/embed/ecdd6b4e5a9848f9a64801ee6141b8c8?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true",
    description: "Der ideale Einstiegskurs für alle, die mit Kindern leben, arbeiten und spielen. Diese neue, kinderleichte Methode ermöglicht es dir, mit nur vier Akkorden unzählige Kinderlieder zu begleiten und Musik spielerisch und zielführend in deinen Alltag zu integrieren.",
    lernform: "Dieser Kurs steht dir als 'Selbstlernmodul' zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten.",
    instructor: "Anne Lüken",
    duration: "ca. 6 Zeitstunden plus Live-Meetings bei Bedarf",
  },
  {
    id: 9,
    title: "Grundkurs Akkorde",
    subtitle: "Alle Basis-Akkorde in einem Kurs",
    category: "Selbstlernmodule",
    image: gitarreGrundkursAkkorde,
    videoUrl: "https://www.loom.com/embed/7ae85ea8ca8a4b16b3a4bf2574b6e52b?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true",
    description: "Nach dem Gitarren-Kickstart lernst du hier alle weiteren wichtigen Akkorde, die du für die Begleitung von Kinderliedern und Songs brauchst. In Praxisvideos üben wir den Wechsel gemeinsam immer und immer wieder, bis er funktioniert. In jedem Kapitel spielen wir gemeinsam neue Lieder, in denen du die gelernten Akkorde und Schlagtechniken direkt anwenden kannst.",
    content: "- Alle Lieder als PDF Download\n- Lernvideos zur Erklärung\n- Videos zum gemeinsamen Üben\n- Lernen rund um die Uhr",
    lernform: "Dieser Kurs steht dir als 'Selbstlernmodul' zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten.",
    instructor: "Daniel Sommer",
    duration: "ca. 7 Zeitstunden plus Übezeit",
  },
  {
    id: 10,
    title: "Aufbaukurs Barrégriffe & Kapodaster",
    subtitle: "Hier lernst du, wie das funktioniert",
    category: "Selbstlernmodule",
    image: gitarreAufbaukursBarregriffe,
    videoUrl: "https://www.loom.com/embed/44d5db9c883043908c44748326825dea?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true",
    description: "Endlich jedes Lied auf der Gitarre begleiten! In diesem Kurs lernst du die wichtigsten theoretischen Basics um das komplette Griffbrett zu verstehen. Dies ist Voraussetzung, um Barré-Griffe (Quergriffe) auf der Gitarre zu verstehen und anzuwenden. Du lernst die Technik des Barré-Griffs Schritt für Schritt und kannst am Ende dadurch (fast) alle Akkorde greifen, die du nicht nur für jedes Kinderlied, sondern auch für Popsongs und vieles mehr benötigst.",
    content: "- Neue Lieder inkl. PDF Download\n- Einsatz des Kapodasters\n- Wie funktioniert der Barrégriff\n- Lernvideos zur Erklärung\n- Videos zum gemeinsamen Üben\n- Lernen rund um die Uhr",
    lernform: "Dieser Kurs steht dir als 'Selbstlernmodul' zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten.",
    instructor: "Daniel Sommer",
    duration: "ca. 7 Zeitstunden + Übezeit",
  },
  {
    id: 11,
    title: "Der große Rhythmuskurs",
    subtitle: "Lerne alle wichtigen Schlag- und Zupfmuster für die...",
    category: "Selbstlernmodule",
    image: gitarreRhythmuskurs,
    videoUrl: "https://www.loom.com/embed/fdfe799bd6734d098a63df24a6253c35?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true",
    description: "In diesem Kurs dreht sich alles um das Erlernen von Schlag- und Zupfmustern für die Gitarrenbegleitung. Ganz gleich ob du Anfänger bist oder schon länger spielst - hier lernst du alles 'von der Pike auf', um deine Liedbegleitung entsprechend groovig zu begleiten.",
    content: "- Alle Lieder als PDF Download\n- Teilnahmezertifikat zum Ausdrucken\n- Lernvideos zur Erklärung\n- Videos zum gemeinsamen Üben\n- Lernen rund um die Uhr",
    lernform: "Dieser Kurs steht dir als 'Selbstlernmodul' zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten.",
    instructor: "Daniel Sommer",
    duration: "ca. 7 Zeitstunden + Übezeit",
  },
  {
    id: 12,
    title: "Aufbaukurs Akkorde",
    subtitle: "Mehr Akkorde für Fortgeschrittene",
    category: "Selbstlernmodule",
    image: gitarreAufbaukursAkkorde,
    videoUrl: "https://www.loom.com/embed/61fd14f437654d8585d8ceac9ecbefa7?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true",
    description: "Der Aufbaukurs für Fortgeschrittene. In diesem Kurs lernst du die sog. Sept-Akkorde und weitere Akkordvariationen zu greifen und anzuwenden. Wenn du die grundlegenden Akkorde bereits beherrschst, ist es interessant die Akkorde der Greifhand zu erweitern, weil du deinem Spiel dadurch mehr Klang-Farbe gibst. Es gibt noch viele interessante Akkorde, die sich einfach toll anhören und Spaß machen zu spielen.",
    content: "- Neue Lieder inkl. PDF Download\n- Sept-Akkorde\n- Lernvideos zur Erklärung\n- Videos zum gemeinsamen Üben\n- Lernen rund um die Uhr",
    lernform: "Dieser Kurs steht dir als 'Selbstlernmodul' zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten.",
    instructor: "Daniel Sommer",
    duration: "ca. 7 Zeitstunden + Übezeit",
  },
  {
    id: 13,
    title: "Liederwerkstatt",
    subtitle: "regelmäßige Zoom-Meetings mit der Community",
    category: "Liederwerkstatt",
    image: gitarreLiederwerkstatt,
    description: "In der MusiKonzept-Liederwerkstatt hast du die Möglichkeit im 14-tägigen Rhythmus live auf Zoom mit der Community gemeinsam zu lernen. Du lernst jeweils ein neues Kinderlied inkl. Begleitmuster und hast so die Gelegenheit 'am Ball zu bleiben'. Du kannst Fragen stellen und bist im direkten Austausch mit deiner Dozentin. Klick dich einfach rein, wenn du magst.",
    lernform: "Die Liederwerkstatt findet 14-tägig live online über Zoom statt.",
    instructor: "Anne Lüken",
    duration: "14-tägig live auf Zoom",
  },
  {
    id: 14,
    title: "12 Kreisspiel- und Bewegungslieder",
    subtitle: "Lerne 12 einfache Lieder auf der Gitarre zu begleiten",
    category: "Selbstlernmodule",
    image: gitarreKreisspiele,
    description: "12 Kreisspiel- und Bewegungslieder für den Kita-Alltag. Lerne diese Lieder auf der Gitarre zu begleiten mit verschiedenen Rhythmusfiguren und Zupfmustern von einfach bis komplex. Außerdem bekommst du, je nach Lied, noch Spielanregungen für den pädagogischen Alltag in Kita & Schule um mit dem Lied auch wirklich Spaß mit den Kindern zu haben.",
    content: "- Alle Lieder als PDF Download\n- Teilnahmezertifikat zum Ausdrucken\n- Lernvideos zur Erklärung\n- Videos zum gemeinsamen Üben\n- Lernen rund um die Uhr",
    lernform: "Dieser Kurs steht dir als 'Selbstlernmodul' zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten.",
    instructor: "Daniel Sommer",
    duration: "ca. 7 Zeitstunden + Übezeit",
  },
  {
    id: 15,
    title: "12 alte & bekannte Kinderlieder begleiten",
    subtitle: "Lerne 12 bekannte Kinderlieder auf der Gitarre...",
    category: "Selbstlernmodule",
    image: gitarreKinderlieder,
    description: "In diesem Kurs lernst du die Begleitung von 12 Klassikern, die jeder kennt.",
    content: "- Neue Lieder inkl. PDF Download\n- Sept-Akkorde\n- Lernvideos zur Erklärung\n- Videos zum gemeinsamen Üben\n- Lernen rund um die Uhr",
    lernform: "Dieser Kurs steht dir als 'Selbstlernmodul' zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten.",
    instructor: "Daniel Sommer",
    duration: "ca. 7 Zeitstunden + Übezeit",
  },
  {
    id: 16,
    title: "5 Zupfmuster für die Liedbegleitung",
    subtitle: "Ein Kurs mit verschiedenen Zupfmustern, um deine...",
    category: "Selbstlernmodule",
    image: gitarreZupfmuster,
    description: "Zupfen bedeutet, dass du die Saiten der Gitarre einzeln nacheinander anschlägst. Hierbei ist es wichtig, dass du von Anfang an auf die richtige Technik achtest, denn sonst kannst du viel falsch machen. Du lernst hier die Grundlagen für die rechte Hand und einige Variationen für den 3/4 und 4/4 Takt.",
    content: "- 5 verschiedene Zupfmuster\n- Lernvideos zur Erklärung\n- Lernen rund um die Uhr",
    lernform: "Dieser Kurs steht dir als 'Selbstlernmodul' zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten.",
    instructor: "Daniel Sommer",
    duration: "ca. 3 Zeitstunden plus Übezeit",
  },
  {
    id: 17,
    title: "Schlagmuster für die Liedbegleitung",
    subtitle: "Ein Kurs mit verschiedenen Schlagmustern, um deine...",
    category: "Selbstlernmodule",
    image: gitarreSchlagmuster,
    description: "Neuer Schwung für deine Liedbegleitung. Hier lernst du viele verschiedene Schlag- und Zupfmuster um deine Liedbegleitung interessanter zu gestalten.",
    content: "- Verschiedene Schlagmuster\n- Lernvideos zur Erklärung\n- Lernen rund um die Uhr",
    lernform: "Dieser Kurs steht dir als 'Selbstlernmodul' zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten.",
    instructor: "Daniel Sommer",
    duration: "ca. 5 Zeitstunden plus Übezeit",
  },
];

const FachkraftGitarre = () => {
  const [selectedModule, setSelectedModule] = useState<typeof modules[0] | null>(null);
  const [expandedTestimonial, setExpandedTestimonial] = useState<number | null>(null);

  const testimonials = [
    {
      id: 1,
      name: "Daniela Hartmann",
      role: "Kita St. Antonius Oberselters",
      image: danielaHartmann,
      shortText: "Die Fortbildung war für mich sehr informativ und interessant. Ich bin komplett als \"Neuling\" gestartet, da ich zuvor keine Gitarre spielen konnte. Allerdings war es mir nach dem ersten Meeting bereits möglich, leichte Lieder zu begleiten...",
      fullText: "Die Fortbildung war für mich sehr informativ und interessant. Ich bin komplett als \"Neuling\" gestartet, da ich zuvor keine Gitarre spielen konnte. Allerdings war es mir nach dem ersten Meeting bereits möglich, leichte Lieder zu begleiten. Während der Fortbildung habe ich viele Anregungen erhalten, wie ich die Gitarre vielfältig im Kindergarten einsetzen kann und auch die Musiktheorie wurde gut übermittelt. Der Kurs wurde von Daniel sehr interessant und ansprechend gestaltet, Fragen wurden miteingebunden und mit viel Fachwissen beantwortet."
    },
    {
      id: 2,
      name: "Fatma Ünal",
      role: "Familienzentrum Fiedelbär Mülheim/Ruhr",
      image: fatmaUenal,
      shortText: "Hallo, ich heiße Fatma Ünal bin Erzieherin und arbeite im Familienzentrum Fiedelbär in Mülheim an der Ruhr. Der online Gitarrenkurs hat mir sehr viel Spaß gemacht und ich habe mit einfachen Griffen gelernt Gitarre zu spielen...",
      fullText: "Hallo, ich heiße Fatma Ünal bin Erzieherin und arbeite im Familienzentrum Fiedelbär in Mülheim an der Ruhr. Der online Gitarrenkurs hat mir sehr viel Spaß gemacht und ich habe mit einfachen Griffen gelernt Gitarre zu spielen. Viele neue Lieder konnte ich auch umsetzen und spielen. Ich hätte nie gedacht das es so leicht sein kann, wobei ich sagen muss ohne Üben gehts nicht. Daniel hat alles sehr gut erklärt und sich für jeden von uns mit seinen Wünschen und Fragen Zeit genommen. Ich kann jedem, der auf leichte Art und Weise Gitarre spielen lernen möchten diesen Kurs ans Herz legen."
    },
    {
      id: 3,
      name: "Sandy Trinkies",
      role: "Grundschullehrerin",
      image: sandyTrinkies,
      shortText: "Mit viel Spaß nahm ich am Onlinekurs „Gitarre leicht lernen\" teil. Zu Beginn wurden wir gut mit tollen Liederbüchern, Kappo und Stimmgerät ausgestattet. Außerdem gibt es einen großen Fundus online mit vielen praxisnahen Ideen...",
      fullText: "Mit viel Spaß nahm ich am Onlinekurs „Gitarre leicht lernen\" teil. Zu Beginn wurden wir gut mit tollen Liederbüchern, Kappo und Stimmgerät ausgestattet. Außerdem gibt es einen großen Fundus online mit vielen praxisnahen Ideen. Die anderen Teilnehmer und auch Daniel der Kursleiter begegneten mir so persönlich, als würden wir direkt beisammen sitzen und gemeinsam singen, musizieren und lernen. Daniel motivierte uns immer wieder mit seiner tollen Art zum Üben und passte den Unterricht an alle Teilnehmer an. Bei Fragen auch außerhalb der Seminare war Daniel immer zur Stelle. So erlernte ich nicht nur das Spielen auf der C-Gitarre, sondern konnte auch beginnen Gitarre in der Standard-Stimmung zu trainieren. Mit Freude nutze ich nun in der Grundschule neben dem Klavier die Gitarre, auch mein Kleiner Zuhause ist begeistert."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO title={`Fachkraft für Gitarre – Ausbildung | MusiKonzept`} description={`Berufsbegleitende Ausbildung zur Fachkraft für Gitarre – online lernen mit Live-Webinaren, Prüfung und Zertifikat.`} path="/ausbildung/fachkraft-gitarre" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-hero-gradient-start via-background to-hero-gradient-end opacity-60" />

        {/* Animierte Hintergrund-Blobs in Markenfarben */}
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-primary/15 blur-3xl animate-blob-drift pointer-events-none" />
        <div className="absolute top-1/3 -right-32 w-[480px] h-[480px] rounded-full bg-accent/15 blur-3xl animate-blob-drift-delayed pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-[360px] h-[360px] rounded-full bg-yellow-300/10 blur-3xl animate-blob-drift pointer-events-none" />

        {/* Floating decorative elements */}
        <div className="absolute top-40 right-0 w-[200px] md:w-[400px] h-[200px] md:h-[400px] opacity-[0.05] animate-float pointer-events-none hidden md:block">
          <img src={notesImg} alt="" className="w-full h-full object-contain" />
        </div>

        {/* Verspielter Notenschlüssel oben links */}
        <div
          className="absolute top-28 left-4 md:left-12 text-accent/70 -rotate-12 animate-float pointer-events-none hidden sm:block"
          aria-hidden="true"
        >
          <Music className="w-12 h-12 md:w-16 md:h-16 drop-shadow-md" strokeWidth={1.5} />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-[1.3fr,0.7fr] gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" aria-hidden="true" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
                  Zertifikatskurs für päd. Fachkräfte
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight">
                <span className="block text-foreground">Der große</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  Kita-Gitarrenkurs
                </span>
              </h1>

              <p className="text-xl text-accent font-semibold leading-relaxed max-w-2xl">
                Liedbegleitung in Kita &amp; Krippe
              </p>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                Du willst Kindern mit der Gitarre begleiten – sicher, 
                entspannt und mit echtem Repertoire für den Alltag. 
                In 7 Live-Webinaren, mit Selbstlernkursen und der 
                Liederwerkstatt begleitest du Lieder in Krippe und Kita 
                – und schließt mit einem offiziellen MusiKonzept-Zertifikat ab.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4 items-start">
                <Button 
                  size="lg" 
                  className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
                  asChild
                >
                  <Link to="/ausbildung/fachkraft-gitarre/kaufen">
                    Jetzt anmelden
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full px-8 border-2 hover:bg-accent/10 transition-all duration-300 hover:scale-105 group"
                  asChild
                >
                  <a href="/webinar/kita-gitarrenkurs" rel="noopener noreferrer">
                    Kostenloses Infowebinar
                    <PlayCircle className="ml-2 h-5 w-5 transition-transform group-hover:scale-110" />
                  </a>
                </Button>
              </div>
            </div>
            
            {/* Right Image with Floating Badge */}
            <div className="relative animate-fade-in mt-8 md:mt-0 lg:scale-110 lg:-mr-8" style={{ animationDelay: "0.2s" }}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] md:aspect-[3/4] max-h-[280px] md:max-h-[720px] max-w-md md:max-w-none mx-auto">
                <img 
                  src={heroImage} 
                  alt="Kita-Gitarrenkurs" 
                  className="w-full h-full object-cover"
                />
                {/* Decorative Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
              </div>

              {/* Runder Floating Badge: Einstieg jederzeit möglich */}
              <div className="absolute -top-2 -right-2 md:-top-4 md:-right-4 lg:-top-6 lg:-right-6 z-20 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                <div className="relative">
                  {/* Sanfter Glow */}
                  <div className="absolute inset-0 rounded-full bg-accent/40 blur-2xl scale-110 animate-pulse" aria-hidden="true" />
                  <div className="relative w-20 h-20 md:w-36 md:h-36 lg:w-44 lg:h-44 rounded-full bg-gradient-to-br from-accent to-yellow-400 text-accent-foreground shadow-2xl flex flex-col items-center justify-center text-center p-2 md:p-4 -rotate-6 hover:rotate-0 transition-transform duration-500">
                    <span className="text-xs md:text-base lg:text-lg font-bold uppercase tracking-wide leading-tight">Einstieg<br/>jederzeit<br/>möglich</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Das kannst du danach Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/20 to-background" />

        {/* Floating decorative element */}
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] opacity-[0.06] animate-float-delayed pointer-events-none">
          <img src={drumImg} alt="" className="w-full h-full object-contain" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-6">
              Das kannst du danach
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                text: "Du begleitest Kinderlieder sicher auf der Gitarre – auch ohne jahrelange Vorübung",
                icon: Music,
                iconBg: "bg-primary/15",
                iconColor: "text-primary",
                ring: "group-hover:ring-primary/30",
              },
              {
                text: "Du hast ein breites Repertoire für Krippe und Kita",
                icon: BookOpen,
                iconBg: "bg-accent/15",
                iconColor: "text-accent",
                ring: "group-hover:ring-accent/30",
              },
              {
                text: "Du weißt, wie du Gitarre spielerisch in den Gruppenalltag einbaust",
                icon: Sparkles,
                iconBg: "bg-yellow-400/20",
                iconColor: "text-yellow-600",
                ring: "group-hover:ring-yellow-400/30",
              },
              {
                text: "Du hast ein offizielles MusiKonzept-Zertifikat – berufsbegleitend erworben",
                icon: Award,
                iconBg: "bg-emerald-500/15",
                iconColor: "text-emerald-600",
                ring: "group-hover:ring-emerald-500/30",
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card
                  key={idx}
                  className={`group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-border/50 bg-card/90 backdrop-blur-sm animate-fade-in ring-1 ring-transparent ${item.ring}`}
                  style={{ animationDelay: `${0.1 * (idx + 1)}s` }}
                >
                  <CardContent className="p-8 space-y-4">
                    <div className={`w-16 h-16 rounded-2xl ${item.iconBg} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 shadow-sm`}>
                      <Icon className={`w-8 h-8 ${item.iconColor}`} strokeWidth={2} />
                    </div>
                    <p className="text-foreground font-semibold leading-relaxed">
                      {item.text}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section moved further down */}

      {/* Dein Weg zur Fachkraft Section */}
      <GitarreInteractivePathSection />

      {/* Termine Section */}
      <GitarreTermineSection />

      {/* Berufsbegleitende Weiterbildung Section */}
      <section className="py-24 relative overflow-hidden bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Certificate Image */}
            <div className="relative animate-fade-in">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={zertifikatImage.url} 
                  alt="Zertifikat Die Gitarre im pädagogischen Handlungsfeld" 
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Right: Content */}
            <div className="space-y-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold leading-tight">
                Dein Zertifikat:{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  Die Gitarre im pädagogischen Handlungsfeld
                </span>
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed">
                Der berufsbegleitende Kurs "Der große Kita-Gitarrenkurs" 
                richtet sich an alle, die mit Kindern leben, arbeiten oder spielen – ob Erzieher:innen, 
                Lehrer:innen, Kursleiter:innen oder Eltern. Die Weiterbildung vermittelt fundierte 
                Grundlagen des Gitarrenspiels, ein breites Liedrepertoire und kreative Begleittechniken 
                für die pädagogische Praxis.
              </p>
              
              <p className="text-lg text-muted-foreground leading-relaxed">
                Der Kurs kombiniert Selbstlernmodule in der Online-Akademie mit Live-Webinaren, 
                in denen du Fragen stellen und dich mit anderen Teilnehmenden austauschen kannst. 
                Zusätzlich besteht die Möglichkeit, an der{" "}
                <span className="font-semibold text-foreground">MusiKonzept Liederwerkstatt</span>{" "}
                teilzunehmen, um regelmäßig neue Lieder zu erlernen.
              </p>

              {/* Details List */}
              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <p className="text-lg">
                    <span className="font-semibold text-foreground">Dauer:</span>{" "}
                    <span className="text-muted-foreground">
                      6–24 Monate (flexible Zeiteinteilung, abhängig von Vorkenntnissen und Übungszeit)
                    </span>
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <p className="text-lg">
                    <span className="font-semibold text-foreground">Kosten:</span>{" "}
                    <span className="text-muted-foreground">
                      {GITARRE_PREIS}, für MusiKonzept-Mitglieder {GITARRE_MITGLIED} (ihr eigenes Abo ruht ab der Buchung ein Jahr)
                    </span>
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <p className="text-lg">
                    <span className="font-semibold text-foreground">Zielgruppe:</span>{" "}
                    <span className="text-muted-foreground">
                      Erzieher:innen, Lehrer:innen, Kursleiter:innen, Eltern
                    </span>
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <p className="text-lg">
                    <span className="font-semibold text-foreground">Abschluss:</span>{" "}
                    <span className="text-muted-foreground">
                      Zertifikat "Die Gitarre im pädagogischen Handlungsfeld" (nach bestandener Prüfung)
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section (moved: nach Berufsbegleitende Weiterbildung, vor Lernmodule) */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--gradient-start))] via-background to-[hsl(var(--gradient-end))] opacity-70" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />

        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-block mb-4">
              <Badge className="bg-gradient-to-r from-accent to-primary text-white border-0 px-6 py-2 text-sm font-semibold">
                Stimmen aus der Praxis
              </Badge>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-6 text-foreground">
              Das sagen Teilnehmerinnen
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-card/95 backdrop-blur-sm border-2 border-primary/20 shadow-lg hover:shadow-2xl hover:border-primary/40 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {expandedTestimonial === testimonial.id ? testimonial.fullText : testimonial.shortText}
                  </p>
                  <button
                    onClick={() => setExpandedTestimonial(expandedTestimonial === testimonial.id ? null : testimonial.id)}
                    className="text-primary hover:text-primary/80 font-semibold mb-6 flex items-center gap-2 transition-colors"
                  >
                    {expandedTestimonial === testimonial.id ? "Weniger lesen" : "Mehr lesen"}
                    <ArrowRight className={`w-4 h-4 transition-transform ${expandedTestimonial === testimonial.id ? "rotate-90" : ""}`} />
                  </button>
                  <div className="flex items-center gap-4 pt-4 border-t border-border/50">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-primary/30 shadow-lg"
                    />
                    <div>
                      <p className="font-bold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Lernmodule Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background to-muted/30" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16 space-y-4 animate-fade-in">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
              Lernmodule
            </Badge>
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground">
              Deine{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary">
                Lernmodule
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Hier findest du eine Übersicht über die verschiedenen Lernmodule, auf die du im Rahmen des 
              Kurses Zugriff hast. Klicke auf eine Kachel, um mehr zu erfahren.
            </p>
          </div>
          
          {/* Modules Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {modules.map((module, index) => {
              const isSelbstlernmodul = module.category === "Selbstlernmodule";
              const isLiederwerkstatt = module.category === "Liederwerkstatt";
              
              return (
                <Card 
                  key={module.id}
                  className={`group relative backdrop-blur-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden cursor-pointer border-0 animate-fade-in ${
                    isSelbstlernmodul ? "bg-accent/5" : isLiederwerkstatt ? "bg-primary/5" : "bg-card/50"
                  }`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => setSelectedModule(module)}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden">
                    <img 
                      src={module.image} 
                      alt={module.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    
                    {/* Play Icon Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-xl ${
                        isSelbstlernmodul ? "bg-accent/90" : "bg-primary/90"
                      }`}>
                        <PlayCircle className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 right-4">
                      <Badge className={`backdrop-blur-sm ${
                        isSelbstlernmodul 
                          ? "bg-accent/90 text-accent-foreground" 
                          : isLiederwerkstatt
                          ? "bg-primary/90 text-primary-foreground"
                          : "bg-primary/90 text-primary-foreground"
                      }`}>
                        {module.category}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <CardContent className="p-6 space-y-2">
                    <h3 className={`text-lg font-bold transition-colors ${
                      isSelbstlernmodul 
                        ? "text-foreground group-hover:text-accent" 
                        : "text-foreground group-hover:text-primary"
                    }`}>
                      {module.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {module.subtitle}
                    </p>
                  </CardContent>
                  
                  {/* Hover Arrow */}
                  <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowRight className={`w-5 h-5 ${
                      isSelbstlernmodul ? "text-accent" : "text-primary"
                    }`} />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Module Dialog/Popup */}
      <Dialog open={!!selectedModule} onOpenChange={() => setSelectedModule(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedModule && (
            <>
              <DialogHeader>
                <div className="mb-4">
                  <Badge className="bg-primary/10 text-primary mb-3">
                    {selectedModule.category}
                  </Badge>
                  <DialogTitle className="text-3xl font-bold text-foreground mb-2">
                    {selectedModule.title}
                  </DialogTitle>
                  <DialogDescription className="text-lg text-muted-foreground">
                    {selectedModule.subtitle}
                  </DialogDescription>
                </div>
              </DialogHeader>
              
              {/* Module Image or Video */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl mb-6">
                {selectedModule.videoUrl ? (
                  <div className="relative aspect-video">
                    <iframe 
                      src={selectedModule.videoUrl} 
                      frameBorder="0" 
                      allowFullScreen
                      className="absolute top-0 left-0 w-full h-full"
                    />
                  </div>
                ) : (
                  <img 
                    src={selectedModule.image} 
                    alt={selectedModule.title}
                    className="w-full h-auto"
                  />
                )}
              </div>
              
              {/* Module Description */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-primary" />
                    Über dieses Modul
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {selectedModule.description}
                  </p>
                </div>
                
                {/* Module Content/Inhalte */}
                {selectedModule.content && (
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      Inhalt:
                    </h3>
                    <div className="space-y-2">
                      {selectedModule.content.split('\n').map((item, idx) => (
                        <p key={idx} className="text-muted-foreground leading-relaxed">
                          {item}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Module Details */}
                {(selectedModule.instructor || selectedModule.duration) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedModule.instructor && (
                      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-0">
                        <CardContent className="p-6 space-y-2">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="w-4 h-4 text-primary" />
                            <span>Dozent/in</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {selectedModule.instructor}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                    
                    {selectedModule.duration && (
                      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-0">
                        <CardContent className="p-6 space-y-2">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="w-4 h-4 text-primary" />
                            <span>Dauer</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {selectedModule.duration}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
                
                {/* Lernform Info - nur für Selbstlernmodule */}
                {selectedModule.lernform && (
                  <Card className="bg-accent/10 border border-accent/20">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <BookOpen className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-foreground mb-2">Lernform</h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {selectedModule.lernform}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Section */}
      <section className="py-24 relative overflow-hidden bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Left: Team Photo */}
            <div className="relative animate-fade-in">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={teamPhoto} 
                  alt="Anne Lüken und Daniel Sommer - Dein Team für den großen Kita-Gitarrenkurs" 
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Right: Text Content */}
            <div className="space-y-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                  Dein Team hinter dem Kurs
                </h2>
                
                <p className="text-xl text-accent font-semibold">
                  Dein Team hinter dem großen Kita-Gitarrenkurs
                </p>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed">
                Anne Lüken, eine erfahrene Diplom-Musiklehrerin mit jahrelanger Praxis im Gitarrenunterricht 
                und in der musikalischen Arbeit mit Kindern, leitet diesen Kurs. Unterstützt wird sie durch 
                die hochwertigen Kurse und Lernmaterialien des erfahrenen Gitarrenlehrers und MusiKonzept-Gründers 
                Daniel Sommer. Mit ihrer Expertise und Leidenschaft für Musik begleiten sie dich auf deinem Weg, 
                deine Gitarren- und pädagogischen Fähigkeiten auszubauen und deine Begeisterung für die Musik 
                erfolgreich in der Arbeit mit Kindern einzusetzen.
              </p>

              {/* Signatures */}
              <div className="pt-4">
                <img 
                  src={teamSignatures} 
                  alt="Unterschriften von Daniel und Anne" 
                  className="h-16 w-auto opacity-70"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ & Testimonials Combined Section with unified gradient background */}
      <div className="relative overflow-hidden">
        {/* Unified gradient background for both sections */}
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--gradient-start))] via-background to-[hsl(var(--gradient-end))] opacity-70" />
        
        {/* Decorative circles */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />

        {/* FAQ Section */}
        <section className="py-24 relative">
          <div className="container mx-auto px-6 max-w-4xl relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <div className="inline-block mb-4">
                <Badge className="bg-gradient-to-r from-primary to-accent text-white border-0 px-6 py-2 text-sm font-semibold">
                  FAQ
                </Badge>
              </div>
               <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-6 text-foreground">
                Häufig gestellte Fragen
              </h2>
              <p className="text-xl text-muted-foreground">
                Hier findest du Antworten auf die wichtigsten Fragen zum Kurs
              </p>
            </div>

          <Accordion type="single" collapsible className="space-y-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <AccordionItem value="item-1" className="bg-card/95 backdrop-blur-sm border-2 border-primary/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-primary/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Muss ich bereits Erfahrung im Gitarrenspiel haben?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Nein, der Kurs ist sowohl für Anfänger:innen als auch für Fortgeschrittene geeignet. Du kannst dein Lerntempo flexibel anpassen und unsere Kurse holen dich genau dort ab, wo du stehst – egal, ob du erste Akkorde lernst oder dein Spiel perfektionieren möchtest.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-card/95 backdrop-blur-sm border-2 border-accent/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-accent/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Wie viel Zeit sollte ich für den Kurs einplanen?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Das hängt ganz von deinen Vorkenntnissen und deinem Übungspensum ab. Wenn du bereits Erfahrung hast, kannst du den Kurs in 6 Monaten absolvieren. Anfänger:innen, die mehr Zeit zum Üben benötigen, haben bis zu 24 Monate Zeit.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-card/95 backdrop-blur-sm border-2 border-primary/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-primary/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Wie funktioniert die Kombination aus Selbstlernmodulen und Live-Webinaren?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Die Selbstlernmodule stehen dir jederzeit in der Online-Akademie zur Verfügung und ermöglichen dir flexibles Lernen. In den Live-Webinaren kannst du mit unserer Dozentin Anne Lüken und anderen Teilnehmer:innen Fragen klären, Erfahrungen austauschen und gemeinsam praktische Inhalte vertiefen.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-card/95 backdrop-blur-sm border-2 border-accent/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-accent/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Was passiert, wenn ich ein Live-Webinar verpasse?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Kein Problem! Alle Live-Webinare werden aufgezeichnet, sodass du sie später in deinem eigenen Tempo anschauen kannst.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-card/95 backdrop-blur-sm border-2 border-primary/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-primary/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Erhalte ich ein Zertifikat?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Ja, nach erfolgreicher Absolvierung der Prüfung erhältst du das Zertifikat "Die Gitarre im pädagogischen Handlungsfeld", das deine Fähigkeiten und dein Engagement dokumentiert.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-card/95 backdrop-blur-sm border-2 border-accent/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-accent/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Kann ich während des Kurses auch die MusiKonzept Liederwerkstatt nutzen?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Ja, als Teilnehmer:in des Kurses hast du die Möglichkeit, an der MusiKonzept Liederwerkstatt teilzunehmen. Hier kannst du in regelmäßigen Live-Sessions neue Lieder erlernen und dein Repertoire erweitern.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="bg-card/95 backdrop-blur-sm border-2 border-primary/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-primary/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Was kostet der Kurs?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Der Kurs kostet {GITARRE_PREIS}. Mit einer freigeschalteten MusiKonzept-Mitgliedschaft zahlst du {GITARRE_MITGLIED} (nicht in der Testphase, nicht mit Kompakt oder dem Musik-Kita-Paket). Ein Jahr Mitgliedschaft ist im Kurs enthalten: Bist du schon Mitglied, ruht dein eigenes Abo ab der Buchung zwölf Monate, danach läuft es von selbst weiter. Es fallen keine zusätzlichen Gebühren an und du erhältst Zugang zu allen Materialien und Live-Webinaren.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="bg-card/95 backdrop-blur-sm border-2 border-accent/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-accent/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Kann ich den Kurs neben meinem Beruf absolvieren?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Ja, der Kurs ist berufsbegleitend konzipiert. Dank der flexiblen Selbstlernmodule und der Möglichkeit, Live-Webinare in deinem eigenen Tempo nachzuholen, kannst du die Inhalte problemlos in deinen Alltag integrieren.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-9" className="bg-card/95 backdrop-blur-sm border-2 border-primary/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-primary/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Welche Materialien stehen mir während des Kurses zur Verfügung?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Du erhältst Zugang zu einer umfangreichen Sammlung an Materialien, darunter Gitarrentutorials, Tabulaturen, Schlag- und Zupfmuster sowie Bonusmaterialien, die speziell für den pädagogischen Bereich entwickelt wurden.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-10" className="bg-card/95 backdrop-blur-sm border-2 border-accent/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-accent/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Was passiert, wenn ich den Kurs nicht innerhalb der vorgesehenen Zeit abschließen kann?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Kein Problem! Der Kurs bietet dir einen flexiblen Zeitrahmen von bis zu 24 Monaten. Solltest du dennoch mehr Zeit benötigen, kontaktiere uns einfach – wir finden gemeinsam eine Lösung.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-11" className="bg-card/95 backdrop-blur-sm border-2 border-primary/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-primary/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Welche Gitarre benötige ich für den Kurs?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Für den Kurs wird eine akustische Gitarre empfohlen, idealerweise eine Konzertgitarre mit Nylonsaiten. Sie ist besonders anfängerfreundlich und eignet sich hervorragend für die Liedbegleitung in pädagogischen Kontexten. Wenn du bereits eine andere Art von Gitarre besitzt, kannst du diese in den meisten Fällen ebenfalls verwenden. Bei Unsicherheiten beraten wir dich gerne!
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-12" className="bg-card/95 backdrop-blur-sm border-2 border-accent/20 rounded-2xl px-6 shadow-lg hover:shadow-xl hover:border-accent/40 transition-all">
              <AccordionTrigger className="text-lg font-semibold text-left py-6 hover:no-underline">
                Wie sieht die Prüfung aus?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                Die Prüfung besteht aus einer praktischen und einer theoretischen Einheit. Im praktischen Teil spielst du ausgewählte Kinderlieder und demonstrierst verschiedene Begleittechniken. Im theoretischen Teil zeigst du dein Wissen zu grundlegender Harmonielehre, Akkorden und Liedgestaltung. Alle Prüfungsaufgaben sind praxisnah gestaltet und können online durchgeführt werden. Du wirst während des Kurses optimal darauf vorbereitet!
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
      </div>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary to-accent relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-8 animate-fade-in">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Bereit für deinen großen Kita-Gitarrenkurs?
            </h2>
            <p className="text-xl text-white/90 font-medium">
              890 € · 6–24 Monate · Einstieg jederzeit möglich
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                size="lg"
                className="bg-white text-primary hover:bg-white/90 rounded-full px-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
                asChild
              >
                <Link to="/ausbildung/fachkraft-gitarre/kaufen">
                  Jetzt anmelden
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 border-2 border-white text-white bg-transparent hover:bg-white hover:text-primary transition-all duration-300 hover:scale-105 group"
                asChild
              >
                <a href="/webinar/kita-gitarrenkurs" rel="noopener noreferrer">
                  Kostenloses Infowebinar
                  <PlayCircle className="ml-2 h-5 w-5 transition-transform group-hover:scale-110" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FachkraftGitarre;
